import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  FormControlLabel,
  Switch,
  Typography,
} from '@mui/material';
import { CloudUpload } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import {
  useCreateTrainingContent,
  useUploadTrainingContent,
} from '../../../api/hooks/useTrainingContents';
import { useDataTypes } from '../../../api/hooks/useDataTypes';
import { useOrganization } from '../../../contexts/OrganizationContext';
import { getNextSequence } from '../utils/sequenceHelpers';
import { TrainingContentDto } from '../../../api/types/dtos/training';

interface CreateContentModalProps {
  open: boolean;
  onClose: () => void;
  trainingId: string;
  moduleId: string;
  topicId: string;
  existingContents?: TrainingContentDto[];
}

const CreateContentModal: React.FC<CreateContentModalProps> = ({
  open,
  onClose,
  trainingId,
  moduleId,
  topicId,
  existingContents = [],
}) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [url, setUrl] = useState('');
  const [durationMinutes, setDurationMinutes] = useState<number | ''>('');
  const [progressContribution, setProgressContribution] = useState(100);
  const [file, setFile] = useState<File | null>(null);
  const [useFileUpload, setUseFileUpload] = useState(false);

  const { selectedOrganization } = useOrganization();
  const { enqueueSnackbar } = useSnackbar();

  // Fetch all data types from backend for matching
  const { data: dataTypes, isLoading: dataTypesLoading } = useDataTypes();

  // Debug: Log dataTypes when loaded
  useEffect(() => {
    if (dataTypes) {
      console.log('DataTypes loaded:', dataTypes);
    }
  }, [dataTypes]);

  const createMutation = useCreateTrainingContent(
    selectedOrganization?.id || '',
    trainingId,
    moduleId,
    topicId
  );
  const uploadMutation = useUploadTrainingContent(
    selectedOrganization?.id || '',
    trainingId,
    moduleId,
    topicId
  );

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
    }
  };

  /**
   * Helper function to determine the type name based on file MIME type
   * Following internal convention: type names end with "_url" (e.g., "video_url", "image_url")
   *
   * @param mimeType - The file's MIME type (e.g., "video/mp4", "image/png")
   * @returns The type name string (e.g., "video_url", "image_url")
   */
  const getTypeNameFromMime = (mimeType: string): string => {
    if (mimeType.startsWith('video/')) return 'video_url';
    if (mimeType.startsWith('audio/')) return 'audio_url';
    if (mimeType.startsWith('image/')) return 'image_url';
    if (mimeType === 'application/pdf') return 'pdf_url';
    if (mimeType.includes('document') || mimeType.includes('word')) return 'document_url';
    if (mimeType.includes('spreadsheet') || mimeType.includes('excel')) return 'spreadsheet_url';
    if (mimeType.includes('presentation') || mimeType.includes('powerpoint'))
      return 'presentation_url';
    if (mimeType.startsWith('text/')) return 'text_url';

    // Default to 'file_url' for unknown types
    return 'file_url';
  };

  /**
   * Find the DataType UUID by matching the type name
   * This searches the dataTypes array for a DataType where name === typeName (e.g., "image_url")
   * and returns its UUID
   *
   * @param mimeType - The file's MIME type
   * @returns The UUID of the matching DataType, or null if no match found
   */
  const getDataTypeIdFromMime = (mimeType: string): string | null => {
    if (!dataTypes || dataTypes.length === 0) {
      console.warn('DataTypes not loaded yet');
      return null;
    }

    const typeName = getTypeNameFromMime(mimeType);

    // Find the DataType where name === typeName (e.g., "image_url")
    const matchingType = dataTypes.find((dt) => dt.name === typeName);

    if (!matchingType) {
      console.warn(`No DataType found with name: ${typeName}`);
      return null;
    }

    return matchingType.id; // Return UUID
  };

  /**
   * Get human-readable type name for display
   * Removes "_url" suffix and capitalizes (e.g., "video_url" -> "Video")
   */
  const getDataTypeNameForDisplay = (mimeType: string): string => {
    if (!dataTypes || dataTypes.length === 0) return 'Cargando...';

    const typeName = getTypeNameFromMime(mimeType);
    const matchingType = dataTypes.find((dt) => dt.name === typeName);

    if (!matchingType) return 'Tipo desconocido';

    // Remove _url suffix and capitalize (e.g., "video_url" -> "Video")
    const baseName = matchingType.name.replace('_url', '');
    return baseName.charAt(0).toUpperCase() + baseName.slice(1);
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      enqueueSnackbar('El nombre es requerido', { variant: 'error' });
      return;
    }
    if (!progressContribution || progressContribution < 0 || progressContribution > 100) {
      enqueueSnackbar('La contribución al progreso debe ser entre 0 y 100', {
        variant: 'error',
      });
      return;
    }
    if (!useFileUpload && !url.trim()) {
      enqueueSnackbar('La URL del contenido es requerida', { variant: 'error' });
      return;
    }
    if (useFileUpload && !file) {
      enqueueSnackbar('Debe seleccionar un archivo para subir', { variant: 'error' });
      return;
    }

    try {
      const nextSequence = getNextSequence(existingContents);

      if (useFileUpload && file) {
        // Find the DataType UUID by matching the type name (e.g., "image_url")
        const dataTypeId = getDataTypeIdFromMime(file.type);

        if (!dataTypeId) {
          enqueueSnackbar(
            `No se encontró un DataType con nombre "${getTypeNameFromMime(file.type)}" en el sistema. Por favor, contacte al administrador.`,
            { variant: 'error' }
          );
          return;
        }

        await uploadMutation.mutateAsync({
          file,
          name: name.trim(),
          description: description.trim() || undefined,
          sequence: nextSequence,
          durationMinutes: durationMinutes || undefined,
          progressContribution,
          typeId: dataTypeId, // UUID of the DataType with matching name
        });
      } else {
        // URL mode: default to "text" DataType
        const textDataType = dataTypes?.find((dt) => dt.name === 'text');
        const typeId = textDataType?.id;

        if (!typeId) {
          enqueueSnackbar(
            'No se encontró el DataType "text" en el sistema. Por favor, contacte al administrador.',
            { variant: 'error' }
          );
          return;
        }

        await createMutation.mutateAsync({
          name: name.trim(),
          description: description.trim() || undefined,
          url: url.trim(),
          sequence: nextSequence,
          durationMinutes: durationMinutes || undefined,
          progressContribution,
          topicId,
          typeId,
        });
      }

      enqueueSnackbar('Contenido creado exitosamente', { variant: 'success' });
      onClose();
    } catch (error) {
      enqueueSnackbar('Error al crear el contenido', { variant: 'error' });
      console.error('Error creating content:', error);
    }
  };

  // Reset form when modal opens/closes
  useEffect(() => {
    if (open) {
      setName('');
      setDescription('');
      setUrl('');
      setDurationMinutes('');
      setProgressContribution(100);
      setFile(null);
      setUseFileUpload(false);
    }
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Crear Nuevo Contenido</DialogTitle>
      <DialogContent>
        <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            label="Nombre del Contenido"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            fullWidth
          />
          <TextField
            label="Descripción"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline
            rows={3}
            fullWidth
          />

          <FormControlLabel
            control={
              <Switch
                checked={useFileUpload}
                onChange={(e) => setUseFileUpload(e.target.checked)}
              />
            }
            label="Subir archivo en lugar de URL"
          />

          {!useFileUpload ? (
            <TextField
              label="URL del Contenido"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              required
              fullWidth
              placeholder="https://..."
            />
          ) : (
            <Box>
              <Button
                variant="outlined"
                component="label"
                startIcon={<CloudUpload />}
                fullWidth
                disabled={dataTypesLoading}
              >
                {dataTypesLoading
                  ? 'Cargando tipos...'
                  : file
                  ? 'Cambiar Archivo'
                  : 'Seleccionar Archivo'}
                <input
                  type="file"
                  hidden
                  onChange={handleFileChange}
                  accept="*/*"
                  disabled={dataTypesLoading}
                />
              </Button>
              {dataTypesLoading && (
                <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
                  Cargando tipos de contenido...
                </Typography>
              )}
              {file && !dataTypesLoading && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" display="block">
                    Archivo seleccionado: <strong>{file.name}</strong>
                  </Typography>
                  <Typography
                    variant="caption"
                    display="block"
                    color={getDataTypeIdFromMime(file.type) ? 'success.main' : 'error'}
                  >
                    Tipo detectado: <strong>{getDataTypeNameForDisplay(file.type)}</strong> (
                    {getTypeNameFromMime(file.type)})
                  </Typography>
                  {!getDataTypeIdFromMime(file.type) && (
                    <Typography variant="caption" display="block" color="error" sx={{ mt: 0.5 }}>
                      ⚠️ No hay un DataType con nombre &ldquo;{getTypeNameFromMime(file.type)}&rdquo; en el
                      sistema.
                      <br />
                      Contacte al administrador para agregarlo.
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          )}

          <TextField
            label="Duración (minutos)"
            type="number"
            value={durationMinutes}
            onChange={(e) =>
              setDurationMinutes(e.target.value === '' ? '' : Number(e.target.value))
            }
            fullWidth
            inputProps={{ 
              min: 0,
              onWheel: (e) => e.currentTarget.blur()
            }}
          />
          <TextField
            label="Contribución al Progreso (%)"
            type="number"
            value={progressContribution}
            onChange={(e) => setProgressContribution(Number(e.target.value))}
            required
            fullWidth
            inputProps={{ 
              min: 0, 
              max: 100,
              onWheel: (e) => e.currentTarget.blur()
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary">
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          disabled={createMutation.isPending || uploadMutation.isPending}
        >
          Crear
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateContentModal;

